import { Typography } from '@leapeasy/ui-kit';
import Box from '@mui/material/Box';

/**
 * Claim-form progress bar.
 * Completed / previously reached steps are clickable; future steps are not.
 * Navigation only changes activeStep — form data lives in Redux and is preserved.
 */
export default function CustomStepper({ steps, activeStep = 0, maxReachedStep = 0, onStepClick }) {
  const highestReached = Math.max(activeStep, maxReachedStep);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        {steps.map((step, index) => {
          const isCurrent = index === activeStep;
          const isCompleted = index < activeStep;
          const isReached = index <= highestReached;
          const isFuture = index > highestReached;
          const isClickable = Boolean(onStepClick) && isReached && !isCurrent;

          let backgroundColor = 'rgb(255, 255, 255)';
          let color = 'rgb(36, 34, 37)';
          let border = '1px solid rgb(248, 244, 249)';

          if (isCurrent) {
            backgroundColor = 'rgb(112, 37, 114)';
            color = '#fff';
            border = 'none';
          } else if (isCompleted || (isReached && !isFuture)) {
            backgroundColor = 'rgb(232, 214, 235)';
            color = 'rgb(112, 37, 114)';
            border = 'none';
          } else {
            // Not yet reached — visually muted / non-interactive
            backgroundColor = 'rgb(255, 255, 255)';
            color = 'rgb(163, 154, 169)';
            border = '1px solid rgb(248, 244, 249)';
          }

          return (
            <Box
              key={step.label || index}
              component={isClickable ? 'button' : 'div'}
              type={isClickable ? 'button' : undefined}
              aria-label={
                step.title ? `Go to step ${step.label}: ${step.title}` : `Step ${step.label}`
              }
              aria-current={isCurrent ? 'step' : undefined}
              aria-disabled={isFuture || isCurrent ? true : undefined}
              onClick={() => {
                if (isClickable) {
                  onStepClick(index);
                }
              }}
              sx={{
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor,
                color,
                border,
                borderRadius: '50px',
                fontSize: '12px',
                fontWeight: isCurrent || isCompleted ? 600 : 400,
                cursor: isClickable ? 'pointer' : 'default',
                opacity: isFuture ? 0.55 : 1,
                padding: 0,
                margin: 0,
                fontFamily: 'inherit',
                appearance: 'none',
                WebkitAppearance: 'none',
                '&:hover': isClickable
                  ? {
                      backgroundColor: 'rgb(212, 184, 220)',
                    }
                  : undefined,
                '&:focus-visible': isClickable
                  ? {
                      outline: '2px solid rgb(112, 37, 114)',
                      outlineOffset: '2px',
                    }
                  : undefined,
              }}
            >
              {step.label}
            </Box>
          );
        })}
      </Box>

      {steps[activeStep]?.title && (
        <Box sx={{ marginBottom: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            {steps[activeStep].title}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
